/**
 * FauxDS Badge - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Badge component for prototyping.
 * It is NOT the real @faire/slate Badge and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Badge, see: @faire/slate/components/badge
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 20765:12493 (Slate Badge)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Show Exact Value (true/false)
 * - Overflow handling: values > 99 show "99+" when showExactValue is false
 * - Typography: Paragraph Medium (14px, 500 weight, 20px line-height)
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

export interface BadgeProps {
  /**
   * The numeric value to display
   * @default 10
   */
  value?: number;
  /**
   * Whether to show the exact value (false shows "99+" when value > 99)
   * @default true
   */
  showExact?: boolean;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Badge - FauxDS (recreated from Figma MCP)
 *
 * Badges are usually placed before or after the label of the thing they're quantifying,
 * such as the number of new orders.
 *
 * Usage tips:
 * - Use Badges in conjunction with a single item or label, to avoid ambiguity
 * - Use an overflow (ex 'X+') when the count is larger than three digits
 * - Use Badges to only display numbers. Special characters can be used to represent number values
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ value = 10, showExact: showExactValue = true, className }, ref) => {
    // Determine display value: when showExact is false and value > 99, show "99+"
    const displayValue = !showExactValue && value > 99 ? "99+" : String(value);

    const ariaLabel = displayValue;

    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-[var(--slate-color-surface-tertiary)] px-[var(--slate-dimensions-2)]",
          className
        )}
        style={{
          borderRadius: "var(--slate-radius-component-round)",
          fontFamily: "var(--slate-typography-family-sans-serif)",
          fontSize: "var(--slate-typography-font-size-200)",
          fontWeight: "var(--slate-typography-weight-medium)",
          lineHeight: "var(--slate-typography-line-height-200)",
          letterSpacing: "var(--slate-typography-letter-spacing-200)",
          color: "var(--slate-color-text-primary)",
        }}
        role="status"
        aria-label={ariaLabel}
      >
        {displayValue}
      </span>
    );
  }
);

Badge.displayName = "Badge";

export default Badge;
